
import React from 'react';
import EnhancedSEOSystem from '@/components/seo/EnhancedSEOSystem';
import { useSEOData } from '@/hooks/useSEOData';
import { useLanguage } from '@/context/LanguageContext';

const Privacy = () => {
  const { t } = useLanguage();

  const seoData = useSEOData({
    pageType: 'static',
    customData: {
      title: 'Privacy Policy | Global Therapy Connect - Protecting Your Mental Health Data',
      description: 'Read our comprehensive privacy policy to understand how Global Therapy Connect protects and handles your personal and health information with industry-leading security measures.',
      keywords: 'privacy policy, data protection, mental health privacy, therapy data security, HIPAA compliance, confidential therapy, secure therapy platform, patient privacy rights',
      ogType: 'article',
      ogImage: 'https://psychotherapist.help/og-privacy.png'
    }
  });

  return (
    <>
      <EnhancedSEOSystem
        pageData={seoData}
        pageType="static"
      />

      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">
              Last updated: January 2024
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
              <p className="text-gray-700 mb-4">
                We collect information you provide directly to us, such as when you create an account, 
                schedule appointments, or communicate with therapists through our platform.
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Personal identification information (name, email, phone number)</li>
                <li>Payment and billing information</li>
                <li>Health and therapy-related information</li>
                <li>Communication records with therapists</li>
                <li>Usage data and platform interactions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>To provide and improve our therapy platform services</li>
                <li>To facilitate secure communication between clients and therapists</li>
                <li>To process payments and manage billing</li>
                <li>To ensure platform security and prevent fraud</li>
                <li>To send important service updates and notifications</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Information Sharing</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or rent your personal information to third parties. 
                We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>With your chosen therapist to facilitate treatment</li>
                <li>With payment processors to handle transactions</li>
                <li>When required by law or to protect safety</li>
                <li>With your explicit consent</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
              <p className="text-gray-700">
                We implement industry-standard security measures including end-to-end encryption 
                for video sessions, secure data storage, and regular security audits to protect 
                your personal and health information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
              <p className="text-gray-700 mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Access and review your personal information</li>
                <li>Request corrections to your data</li>
                <li>Delete your account and associated data</li>
                <li>Opt-out of non-essential communications</li>
                <li>Export your data in a portable format</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700">
                If you have questions about this Privacy Policy, please contact us at:
              </p>
              <p className="text-gray-700 mt-4">
                Email: privacy@psychotherapist.help<br />
                Address: Global Therapy Hub Privacy Team
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;
